import {keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import { DateRange } from 'react-day-picker'

import * as apiClient from '@/lib/api'
import {PERMISSIONS, ROLES} from "@/lib/auth/roles";
import { formatDate } from '@/lib/filters/date/date-utils'
import useIsQueryEnabled from '@/lib/hooks/query-enabler';
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import { hasApplicationFeature } from '@/lib/utils'
import {
  ApiQuestion,
  ElevationSummaryResult,
  Metric,
  ProspectConversionData,
} from '@/modules/elevation-dashboard/types'
import { useDate } from '@/stores/date-filter'
import { useServiceType } from '@/stores/service-type'
import {useToken} from "@/stores/token"
import {
  QUERY_KEY_PROSPECT_CONVERSION,
  QUERY_KEY_ELEVATION_AI_SUMMARY,
  QUERY_KEY_ELEVATION_AI_SUMMARY_ID,
  QUERY_KEY_QUESTIONS_BREAKDOWN_ALL,
  QUERY_KEY_QUESTIONS_BREAKDOWN_BY_METRIC,
  QUERY_KEY_THUMB_FEEDBACK_RATING,
} from '@/utils/keys'

function fetchQuestionsBreakdownByMetric(date: DateRange, metric: number, serviceType: number | null, professionalId: number | null): Promise<ApiQuestion[]> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  return apiClient.get(`/firm/elevation/questions/breakdown?metric_id=${metric}&service_type_id=${serviceType}&to=${to}&from=${from}${professionalId ? `&professional_id=${professionalId}` : ''}`)
}

export const questionsBreakdownByMetricQueryOptions = (date: DateRange, metric: number, serviceType: number | null, professionalId: number | null) => queryOptions({
  queryKey: [QUERY_KEY_QUESTIONS_BREAKDOWN_BY_METRIC, date, metric, serviceType, professionalId ],
  queryFn: () => fetchQuestionsBreakdownByMetric(date, metric, serviceType, professionalId),
  placeholderData: keepPreviousData
})

export const useQuestionsBreakdownByMetric = (metric: Metric, professionalId: number | null = null) => {
  const { date } = useDate()
  const { token } = useToken()
  const { serviceType } = useServiceType()

  const queryOptions = questionsBreakdownByMetricQueryOptions(date, metric, serviceType, professionalId)
  queryOptions.enabled = useIsQueryEnabled(!!(token?.roles.includes(ROLES.ROLE_ELEVATION) && serviceType))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchQuestionsBreakdownAll(date: DateRange, serviceType: number | null, professionalId: number | null): Promise<ApiQuestion[]> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  return apiClient.get(`/firm/elevation/questions/breakdown/all?service_type_id=${serviceType}&to=${to}&from=${from}${professionalId ? `&professional_id=${professionalId}` : ''}`)
}

export const questionsBreakdownAllQueryOptions = (date: DateRange, serviceType: number | null, professionalId: number | null) => queryOptions({
  queryKey: [QUERY_KEY_QUESTIONS_BREAKDOWN_ALL, date, serviceType, professionalId],
  queryFn: () => fetchQuestionsBreakdownAll(date, serviceType, professionalId),
  placeholderData: keepPreviousData
})

export const useQuestionsBreakdownAll = (professionalId: number | null = null) => {
  const { date } = useDate()
  const { token } = useToken()
  const { serviceType } = useServiceType()

  const queryOptions = questionsBreakdownAllQueryOptions(date, serviceType, professionalId)
  queryOptions.enabled = useIsQueryEnabled(!!(token?.roles.includes(ROLES.ROLE_ELEVATION) && serviceType))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchProspectConversion(date: DateRange, serviceType: number | null, professionalId: number | null): Promise<ProspectConversionData> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/elevation/scores/prospect-conversion?&to=${to}&from=${from}&service_type_id=${serviceType}${professionalId ? `&professional_id=${professionalId}` : ''}`)
}
export const prospectConversionQueryOptions = (date: DateRange, serviceType: number | null, professionalId: number | null) => queryOptions({
  queryKey: [QUERY_KEY_PROSPECT_CONVERSION, date, serviceType, professionalId],
  queryFn: () => fetchProspectConversion(date, serviceType, professionalId),
  placeholderData: keepPreviousData
})

export const useProspectConversion = (professionalId: number | null = null) => {
  const {date} = useDate()
  const { token } = useToken()
  const {serviceType} = useServiceType()

  const queryOptions = prospectConversionQueryOptions(date, serviceType, professionalId)
  queryOptions.enabled = useIsQueryEnabled(!!(token?.roles.includes(ROLES.ROLE_ELEVATION) && serviceType))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

export const elevationSummaryQueryKey = (
  firmId: number | null | undefined,
  networkId: number | null | undefined,
  professionalId: number | null,
  serviceType: number | null,
  metric: Metric
) => [
  QUERY_KEY_ELEVATION_AI_SUMMARY,
  firmId ?? null,
  networkId ?? null,
  professionalId,
  serviceType,
  metric,
] as const

export const elevationSummaryIdQueryKey = (
  firmId: number | null | undefined,
  networkId: number | null | undefined,
  professionalId: number | null,
  serviceType: number | null,
  metric: Metric
) => [
  QUERY_KEY_ELEVATION_AI_SUMMARY_ID,
  firmId ?? null,
  networkId ?? null,
  professionalId,
  serviceType,
  metric,
] as const

function generateElevationSummary(
  metric: Metric,
  serviceType: number,
  professionalId: number | null
): Promise<ElevationSummaryResult> {
  const queryParams = new URLSearchParams({
    metric_id: String(metric),
    service_type_id: String(serviceType),
  })

  if (professionalId !== null) {
    queryParams.append('professional_id', String(professionalId))
  }

  return apiClient.post(`/firm/ai-analysis/summarize-elevation-dashboard?${queryParams.toString()}`, {})
}

export const useGenerateElevationSummaryMutation = (
  metric: Metric,
  professionalId: number | null = null
) => {
  const queryClient = useQueryClient()
  const { token } = useToken()
  const { serviceType } = useServiceType()

  const summaryQueryKey = elevationSummaryQueryKey(
    token?.firm_id,
    token?.network_id,
    professionalId,
    serviceType,
    metric
  )
  const summaryIdQueryKey = elevationSummaryIdQueryKey(
    token?.firm_id,
    token?.network_id,
    professionalId,
    serviceType,
    metric
  )

  // These queries act as client-side cache slots. They are never directly fetched;
  // instead, the mutation's onSuccess callback populates them via setQueryData.
  const summaryQuery = useQuery<ElevationSummaryResult | null>({
    queryKey: summaryQueryKey,
    queryFn: () => null,
    enabled: false,
  })

  const summaryIdQuery = useQuery<number | null>({
    queryKey: summaryIdQueryKey,
    queryFn: () => null,
    enabled: false,
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (!serviceType) {
        throw new Error('Service type is required to generate an elevation summary')
      }

      return generateElevationSummary(metric, serviceType, professionalId)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(summaryQueryKey, data)
      queryClient.setQueryData(summaryIdQueryKey, data.id)
    },
  })

  return {
    summaryQueryKey,
    summaryIdQueryKey,
    generateElevationSummaryMutation: mutation.mutate,
    generateElevationSummaryMutationAsync: mutation.mutateAsync,
    data: summaryQuery.data ?? null,
    summaryId: summaryIdQuery.data ?? null,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

type UseThumbFeedbackOptions = {
  entityId: number | null
  endpoint: string
  idFieldName?: string
  positiveRating?: number
  negativeRating?: number
}

export const useThumbFeedback = ({
  entityId,
  endpoint,
  idFieldName = 'aiResultId',
  positiveRating = 5,
  negativeRating = 1,
}: UseThumbFeedbackOptions) => {
  const queryClient = useQueryClient()
  const feedbackRatingQueryKey = [QUERY_KEY_THUMB_FEEDBACK_RATING, endpoint, idFieldName, entityId] as const

  // This query acts as a client-side cache slot for the selected rating.
  // It never fetches (enabled: false) to prevent the queryFn from overwriting
  // cached data on mount/refetch cycles. The mutation's onSuccess populates it.
  const { data: selectedRating = null } = useQuery({
    queryKey: feedbackRatingQueryKey,
    queryFn: () => null,
    enabled: false,
    initialData: entityId ? queryClient.getQueryData<number>(feedbackRatingQueryKey) ?? null : null,
  })

  const rateMutation = useMutation({
    mutationFn: async (rating: number) => {
      return apiClient.post(endpoint, {
        [idFieldName]: entityId,
        rating,
      })
    },
    onSuccess: (_, rating) => {
      queryClient.setQueryData(feedbackRatingQueryKey, rating)
    },
  })

  return {
    isPositiveSelected: selectedRating === positiveRating,
    isNegativeSelected: selectedRating === negativeRating,
    isDisabled: rateMutation.isPending,
    onPositiveClick: () => {
      if (selectedRating !== positiveRating) {
        rateMutation.mutate(positiveRating)
      }
    },
    onNegativeClick: () => {
      if (selectedRating !== negativeRating) {
        rateMutation.mutate(negativeRating)
      }
    },
  }
}

/**
 * The elevation AI summary is shown only when the feature is released (env flag) and not switched off by an AI control
 * on the user's firm or network.
 */
export const useCanShowElevationAiSummary = (): boolean => {
  const { permissions } = useToken()

  return hasApplicationFeature('elevation-ai-summary') && permissions.includes(PERMISSIONS.CAN_VIEW_ELEVATION_AI_SUMMARY)
}
